import React, { useEffect, useId, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

export interface FieldProps {
  /** Visible label text. */
  label: string;
  /**
   * `stacked` (default) puts the label above the control — the form layout.
   * `inline` puts label + control on one row (`whitespace-nowrap`) — the toolbar
   * "Display: [n]" pattern. Spec 239: this exists so inline labels stop being
   * hand-rolled per page (the 2026-06-25 Journal 2-row-wrap bug).
   */
  orientation?: 'stacked' | 'inline';
  /** Associates the label with the control. Defaults to the child's `id`, else an auto id injected into the child. */
  htmlFor?: string;
  help?: string;
  error?: string | null;
  className?: string;
  /** The control (Select, Input, …). Field injects an `id` if the child has none. */
  children: React.ReactNode;
  /** Renders a danger-colored `*` next to the label and injects `required`/`aria-required` into the control. */
  required?: boolean;
  /**
   * Whether this field has been interacted with (blurred at least once). Ported
   * from the now-retired FormField.tsx (docs/reference/bug-registry.md's
   * Adoption Ledger). Omit entirely for Field's original behavior — `error`
   * always shows unconditionally the moment it's truthy, exactly as before
   * this prop existed. Pass it (even `false`) to opt a caller into gating:
   * a server-prefilled or not-yet-interacted-with field won't flash "required"
   * at the user before they've had a chance to fill it in.
   */
  touched?: boolean;
  /**
   * Only meaningful once `touched` is passed. Defaults to `true` (matching
   * FormField's own default) — the error is suppressed until `touched` is
   * true. Pass `false` to keep tracking `touched` (e.g. for other UI) without
   * gating the error display on it.
   */
  showErrorOnTouched?: boolean;
}

/**
 * Field — canonical labeled-control wrapper (Spec 239).
 *
 * Replaces the stacking built-in `label` prop on form controls with a single,
 * consistent pattern that supports both `stacked` (forms) and `inline` (toolbars).
 * Label↔control association is handled here (htmlFor / injected id).
 */
export function Field({
  label,
  orientation = 'stacked',
  htmlFor,
  help,
  error,
  className = '',
  children,
  required = false,
  touched,
  showErrorOnTouched = true,
}: FieldProps) {
  const autoId = useId();
  const childId =
    React.isValidElement(children) && (children.props as { id?: string }).id;
  const controlId = htmlFor || childId || autoId;

  // touched === undefined means the caller never opted into gating -- error
  // shows unconditionally, matching Field's behavior before this prop existed.
  const gateOnTouched = touched !== undefined;
  const showError = Boolean(error) && (!gateOnTouched || !showErrorOnTouched || touched);

  const messageId = showError ? `${controlId}-error` : help ? `${controlId}-help` : undefined;

  // Inject the id (when the caller didn't set one) AND forward aria-describedby so
  // screen readers announce help/error text (cross-provider skeptic finding, Spec 239).
  const existingDescribedBy = React.isValidElement(children)
    ? (children.props as { 'aria-describedby'?: string })['aria-describedby']
    : undefined;
  const describedBy = [existingDescribedBy, messageId].filter(Boolean).join(' ') || undefined;
  // Only include a key in the cloneElement config when Field actually wants to force
  // it -- React.cloneElement sets config keys onto the child's props even when their
  // value is `undefined` (it doesn't fall back to the child's own value the way a
  // plain `{...spread}` would), so unconditionally including `required: required ||
  // undefined` here would silently clobber a caller's own `<Input required />` back
  // to unset whenever Field's own `required` prop is the (default) false. Skeptical
  // review caught this live: AccountSwitcher.tsx sets `required` directly on its
  // <Input>s without ever passing it to <Field>, and the unconditional version wiped
  // it. Conditionally spreading avoids the whole clobber class for any future prop
  // added here too, not just this one.
  const control = React.isValidElement(children)
    ? React.cloneElement(
        children as React.ReactElement<{
          id?: string;
          'aria-describedby'?: string;
          'aria-invalid'?: boolean;
          required?: boolean;
          'aria-required'?: boolean;
        }>,
        {
          id: childId || controlId,
          'aria-describedby': describedBy,
          ...(showError ? { 'aria-invalid': true } : {}),
          ...(required ? { required: true, 'aria-required': true } : {}),
        }
      )
    : children;

  // Shake + background-flash micro-interaction on a *live* transition into a SHOWN
  // error state (showError goes falsy -> truthy, or the message changes while already
  // shown). Keyed on `showError`, not raw `error` -- with touched-gating active, the
  // error can already be truthy while suppressed from view, and shaking a field with
  // no visible error text would be a motion cue with no visible cause. Deliberately
  // does NOT fire on initial mount — a server-prefilled error on page load should read
  // as informational, not as a startling "you did something wrong" jolt. `useRef`
  // tracks the previous shown-state across renders; the shake clears itself via
  // onAnimationEnd (not a timeout) so there's no dangling timer and it retriggers
  // cleanly on repeated submissions.
  //
  // `shakeGeneration` (not just the `isShaking` boolean) is what actually drives a
  // retrigger: if a second error arrives while the first shake/flash is still playing,
  // `setIsShaking(true)` on already-`true` state is a no-op React bails on, so the
  // second error would get no visual cue at all. Every real transition bumps the
  // generation counter instead, and a dedicated effect (below) "restarts" the
  // animation for that generation.
  const [isShaking, setIsShaking] = useState(false);
  const [shakeGeneration, setShakeGeneration] = useState(0);
  const shownMessage = showError ? error : null;
  const prevShownMessageRef = useRef(shownMessage);
  const hasMountedRef = useRef(false);
  const shakeWrapperRef = useRef<HTMLDivElement>(null);
  // Computed once at mount (matches the established pattern in Sheet.tsx/Takeover.tsx) —
  // read directly rather than relying solely on the CSS `motion-reduce:` variant so the
  // shake can be asserted on in tests via a mocked `window.matchMedia`. Optional-chained
  // because `matchMedia` can be absent (older WebViews) or return undefined (a test
  // environment's mock reset between tests) — either case should just fall through to
  // "motion is fine," not throw.
  const prefersReducedMotionRef = useRef(
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? !!window.matchMedia('(prefers-reduced-motion: reduce)')?.matches
      : false
  );

  useEffect(() => {
    if (hasMountedRef.current && shownMessage && shownMessage !== prevShownMessageRef.current) {
      setShakeGeneration((g) => g + 1);
    }
    prevShownMessageRef.current = shownMessage;
    hasMountedRef.current = true;
  }, [shownMessage]);

  // Materializes the shake for the current generation. Deliberately NOT a React `key`
  // on the wrapper below — keying it would force React to unmount/remount the control
  // inside it on every retrigger, dropping focus/cursor position mid-interaction (the
  // exact failure mode the permanent, never-conditionally-mounted wrapper below exists
  // to avoid). Instead this restarts the CSS animation on the SAME DOM node: turn the
  // animation off, force a synchronous layout flush so the browser actually commits
  // the "off" state, then turn it back on next frame. This is the standard technique
  // for restarting a still-playing CSS animation without remounting anything
  // (https://css-tricks.com/restart-css-animation/) — changing animation-name (via the
  // class toggle) is what forces a true restart; changing duration/delay alone does not.
  useEffect(() => {
    if (shakeGeneration === 0) return;
    setIsShaking(false);
    const raf = requestAnimationFrame(() => {
      // Reading a layout-dependent property forces the browser to flush pending style
      // recalculation before we re-add the animation classes on the next line — without
      // this the two class changes can get batched into a single paint and the browser
      // never sees a distinct "off" frame to restart from.
      void shakeWrapperRef.current?.offsetWidth;
      setIsShaking(true);
    });
    return () => cancelAnimationFrame(raf);
  }, [shakeGeneration]);

  // The flash (500ms) always outlasts the shake (450ms) and always animates
  // (reduced-motion only suppresses the transform), so it's the reliable signal that
  // the whole micro-interaction is done — guard on animationName since the shake's
  // animationend also bubbles up through this handler.
  const handleControlAnimationEnd = (e: React.AnimationEvent<HTMLDivElement>) => {
    if (e.animationName === 'field-error-flash') {
      setIsShaking(false);
    }
  };

  // Wrap just the control (never the label) so the shake/flash apply to the input
  // itself, not the whole labeled row. The wrapper stays permanently in the tree
  // (never conditionally mounted/unmounted, never keyed) so the control underneath is
  // never remounted — losing focus/cursor position mid-interaction. It's `contents` at
  // rest (removed from the box tree, zero layout/visual footprint at any of Field's
  // 14+ call sites) and becomes a real box only for the ~500ms the animation plays.
  // `p-1 -m-1` gives the flash a visible halo around the control's own opaque
  // background without shifting surrounding layout (padding grows the paintable box;
  // the equal negative margin pulls the outer edge back to where it was).
  const controlNode = (
    <div
      ref={shakeWrapperRef}
      className={twMerge(
        'contents',
        isShaking && 'block relative p-1 -m-1 rounded-lg animate-field-error-flash',
      )}
      onAnimationEnd={handleControlAnimationEnd}
    >
      <div
        className={twMerge(
          'contents',
          isShaking &&
            !prefersReducedMotionRef.current &&
            'block h-full animate-field-shake motion-reduce:animate-none',
        )}
      >
        {control}
      </div>
    </div>
  );

  const labelEl = (
    <label htmlFor={controlId} className="text-sm font-medium text-text-secondary">
      {label}
      {required && <span className="text-danger ml-1" aria-hidden="true">*</span>}
    </label>
  );

  const message = showError ? (
    <p id={messageId} className="text-xs text-danger" role="alert" aria-live="polite">
      {error}
    </p>
  ) : help ? (
    <p id={messageId} className="text-xs text-text-tertiary">
      {help}
    </p>
  ) : null;

  if (orientation === 'inline') {
    return (
      <div className={twMerge('flex flex-col gap-1', className)}>
        <div className="flex items-center gap-2 whitespace-nowrap">
          {labelEl}
          {controlNode}
        </div>
        {message}
      </div>
    );
  }

  return (
    <div className={twMerge('flex flex-col gap-1.5', className)}>
      {labelEl}
      {controlNode}
      {message}
    </div>
  );
}

Field.displayName = 'Field';
